"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import SeoHead from "@/components/SEO/SeoHead";
import CourseRatingWidget from "@/components/Widgets/CourseRatingWidget";
import FAQWidget from "@/components/Widgets/FAQWidget";
import BreadcrumbWidget from "@/components/Widgets/BreadcrumbWidget";
import EnrollCTAWidget from "@/components/Widgets/EnrollCTAWidget";
import RelatedContentWidget from "@/components/Widgets/RelatedContentWidget";
import SocialShareWidget from "@/components/Widgets/SocialShareWidget";
import CourseTestimonialsMini from "@/components/course/CourseTestimonialsMini";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { generateCourseSchema } from "@/utils/schemaGenerators";
import { testimonialsData } from "@/data/testimonialsData";
import { trackViewItem } from "@/utils/gtm";
import { whatsappMessages } from "@/utils/whatsapp";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";
import { CheckCircle, ChevronDown } from "lucide-react";

export interface CourseData {
  slug?: string;
  title?: string;
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[] | string;
    URL?: string;
  };
  hero?: {
    headline?: string;
    subheadline?: string;
  };
  aboutThisCourse?: {
    title?: string;
    content?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  whatYoullLearn?: any[];
  courseHighlights?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CareerOpportunities?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CareerOpportunitiesTwo?: any[];
  WhySeasoftIT?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Features?: any[];
  curriculum?: Array<{
    moduleNumber: number;
    title: string;
    description?: string;
    items?: string[];
  }>;
  eligibilityAndCertification?: {
    prerequisites?: string[];
    certification?: {
      intro?: string;
      items?: string[];
      link?: string;
    };
  };
  whoIsItFor?: string;
  careerOutcomes?: Array<{
    role: string;
    salaryRange: string;
  }>;
  averageRating?: number;
  totalReviews?: number;
  ratingBreakdown?: unknown;
  rating?: {
    average?: number;
    totalReviews?: number;
    total?: number;
    breakdown?: unknown;
  };
  courseDetails?: {
    duration?: string;
    level?: string;
    schedule?: string;
    nextBatch?: string;
  };
  faq?: Array<{ question: string; answer: string }>;
  relatedCourses?: Array<{ title: string; slug: string }>;
  relatedBlogs?: Array<{ title: string; slug: string }>;
  nextBatchStarts?: string;
}

interface CourseDetailTemplateProps {
  courseData: CourseData;
}

export default function CourseDetailTemplate({ courseData }: CourseDetailTemplateProps) {
  const [openModule, setOpenModule] = useState<number | null>(
    courseData?.curriculum?.[0]?.moduleNumber ?? null
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [aggregateRatingSchema, setAggregateRatingSchema] = useState<any>(null);
  const pathname = usePathname();

  const courseName = useMemo(() => {
    if (courseData?.hero?.headline) {
      return courseData?.hero?.headline;
    }
    return "Course Details";
  }, [courseData]);

  const metaKeywords = Array.isArray(courseData?.seo?.keywords)
    ? courseData.seo.keywords.join(", ")
    : courseData?.seo?.keywords || "";

  const courseSlug = pathname?.replace("/courses/", "") ?? "";
  const canonical = `https://seasoftclasses.com/courses/${courseSlug}`;

  const resolvedCourseSlug = useMemo(() => {
    if (courseSlug) {
      return `/courses/${courseSlug}`;
    }

    if (typeof courseData?.slug === "string" && courseData.slug.length > 0) {
      return courseData.slug.startsWith("/courses/")
        ? courseData.slug
        : `/courses/${courseData.slug}`;
    }

    const titleToMatch = `${courseData?.title || ""} ${courseName}`.toLowerCase();
    const matched = testimonialsData.find((item) =>
      titleToMatch.includes(String(item.course || "").toLowerCase())
    );

    return matched?.courseSlug || "";
  }, [courseData, courseName, courseSlug]);

  const courseTestimonials = useMemo(() => {
    if (!resolvedCourseSlug) {
      return [];
    }

    return testimonialsData.filter((item) => item.courseSlug === resolvedCourseSlug);
  }, [resolvedCourseSlug]);

  const courseAggregateRating = useMemo(() => {
    if (courseTestimonials.length === 0) {
      return undefined;
    }

    const total = courseTestimonials.reduce((sum, item) => sum + (item.rating || 0), 0);
    const average = Number((total / courseTestimonials.length).toFixed(1));

    return {
      ratingValue: average,
      reviewCount: courseTestimonials.length,
      bestRating: 5,
      worstRating: 1,
    };
  }, [courseTestimonials]);

  const courseSchema = generateCourseSchema({
    title: courseName,
    description: courseData?.seo?.description,
    slug: `/courses/${courseSlug}`,
    level: courseData?.courseDetails?.level,
    whatYoullLearn: courseData?.whatYoullLearn,
    aggregateRating: courseAggregateRating,
  });

  const schemas = [courseSchema];
  if (aggregateRatingSchema) {
    schemas.push(aggregateRatingSchema);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRatingSchemaReady = useCallback((schema: any) => {
    setAggregateRatingSchema(schema || null);
  }, []);

  const toggleModule = (moduleNumber: number) => {
    setOpenModule((prev) => (prev === moduleNumber ? null : moduleNumber));
  };

  useEffect(() => {
    if (!resolvedCourseSlug) {
      return;
    }

    trackViewItem({
      slug: resolvedCourseSlug,
      title: courseData?.title || courseName,
      category: "Courses",
      price: 0,
    });
  }, [courseData, courseName, resolvedCourseSlug]);

  return (
    <>
      <SeoHead
        currentPath={resolvedCourseSlug || `/courses/${courseSlug}`}
        title={courseData?.seo?.title || courseName}
        description={courseData?.seo?.description}
        keywords={metaKeywords}
        canonical={canonical}
        schema={schemas}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseSchema) }}
      />

      <div className="bg-white min-h-screen pb-24 lg:pb-0">
        <BreadcrumbWidget
          crumbs={[
            { label: "Home", href: "/" },
            { label: "Courses", href: "/courses" },
            { label: courseData?.title || courseName, href: "" },
          ]}
        />

        <section className="relative py-16 md:py-20 lg:py-16 flex items-center bg-[#002147] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#002147] to-[#0b2f6f] opacity-90" />

          <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 relative z-20 w-full text-left">
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              {courseData?.hero?.headline}
            </h1>
            <p className="text-white/90 text-sm md:text-base leading-relaxed max-w-3xl mb-6">
              {courseData?.hero?.subheadline}
            </p>

            <EnrollButton
              label="Enroll Now"
              variant="primary"
              size="md"
              context={FORM_CONTEXTS.COURSE_SIDEBAR}
            />
          </div>
        </section>

        <section className="border-b border-gray-100 bg-[#F9FAFB]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm md:text-base font-semibold text-[#334155]">Have questions about this course?</p>
            <div className="flex flex-wrap items-center gap-3">
              <EnrollButton
                label="Enquire Now"
                variant="outline"
                size="sm"
                context={FORM_CONTEXTS.COURSE_SIDEBAR}
                icon={false}
              />
              <WhatsAppButton
                variant="text-icon"
                size="sm"
                label="Ask on WhatsApp"
                message={whatsappMessages.course(courseData?.hero?.headline || courseName)}
                context="course_hero_bar"
              />
            </div>
          </div>
        </section>

        <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 py-16 md:pt-14 md:py-24">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
            <div className="flex-1 space-y-12 text-left">
              {courseData?.aboutThisCourse && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">
                    {courseData.aboutThisCourse.title}
                  </h2>
                  <p className="text-[#666666] leading-relaxed text-[18px] whitespace-pre-line">
                    {courseData.aboutThisCourse.content}
                  </p>
                </section>
              )}

              <section>
                <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">What You&apos;ll Learn</h2>
                <div className="flex flex-col gap-4">
                  {(courseData?.whatYoullLearn || []).map((item: any) => {
                    if (typeof item === "object" && item?.title) {
                      return (
                        <div key={item.title} className="md:col-span-2 flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                          <div>
                            <p className="font-semibold text-[#1A1A1A]">{item.title}</p>
                            {item.items?.length ? (
                              <ul className="mt-2 ml-5 space-y-1" style={{ listStyleType: "square" }}>
                                {item.items.map((subItem: string) => (
                                  <li key={subItem}>{subItem}</li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={String(item)} className="flex gap-3 text-[#666666] text-[18px]">
                        <CheckCircle size={18} className="text-[#1B61FF] shrink-0" />
                        <span>{item}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {courseData?.courseHighlights && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Course Highlights</h2>
                  <p className="text-[#666666] leading-relaxed text-[18px] whitespace-pre-line">
                    {courseData.courseHighlights}
                  </p>
                </section>
              )}

              {courseData?.CareerOpportunities && courseData.CareerOpportunities.length > 0 && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Career Opportunities with SAP Simple Finance Training</h2>
                  <div className="flex flex-col gap-4">
                    {courseData.CareerOpportunities.map((item: any) => {
                      if (typeof item === "object" && item?.title) {
                        return (
                          <div key={item.title} className="md:col-span-2 flex gap-3 text-[#666666] text-[18px]">
                            <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                            <div>
                              <p className="font-semibold text-[#1A1A1A]">{item.title}</p>
                              {item.items?.length ? (
                                <ul className="mt-2 ml-5 space-y-1" style={{ listStyleType: "square" }}>
                                  {item.items.map((subItem: string) => (
                                    <li key={subItem}>{subItem}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={String(item)} className="flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0" />
                          <span>{item}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {courseData?.CareerOpportunitiesTwo && courseData.CareerOpportunitiesTwo.length > 0 && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">People with SAP Simple Finance can be worked as</h2>
                  <div className="flex flex-col gap-4">
                    {courseData.CareerOpportunitiesTwo.map((item: any) => {
                      if (typeof item === "object" && item?.title) {
                        return (
                          <div key={item.title} className="md:col-span-2 flex gap-3 text-[#666666] text-[18px]">
                            <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                            <div>
                              <p className="font-semibold text-[#1A1A1A]">{item.title}</p>
                              {item.items?.length ? (
                                <ul className="mt-2 ml-5 space-y-1" style={{ listStyleType: "square" }}>
                                  {item.items.map((subItem: string) => (
                                    <li key={subItem}>{subItem}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={String(item)} className="flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0" />
                          <span>{item}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {courseData?.WhySeasoftIT && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Why @ Seasoftclasses IT Only?</h2>
                  <p className="text-[#666666] leading-relaxed text-[18px] whitespace-pre-line">
                    {courseData.WhySeasoftIT}
                  </p>
                </section>
              )}

              {courseData?.Features && courseData.Features.length > 0 && (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Features</h2>
                  <div className="flex flex-col gap-4">
                    {courseData.Features.map((item: any) => {
                      if (typeof item === "object" && item?.title) {
                        return (
                          <div key={item.title} className="md:col-span-2 flex gap-3 text-[#666666] text-[18px]">
                            <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                            <div>
                              <p className="font-semibold text-[#1A1A1A]">{item.title}</p>
                              {item.items?.length ? (
                                <ul className="mt-2 ml-5 space-y-1" style={{ listStyleType: "square" }}>
                                  {item.items.map((subItem: string) => (
                                    <li key={subItem}>{subItem}</li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={String(item)} className="flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0" />
                          <span>{item}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {courseData?.curriculum?.length ? (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Curriculum</h2>
                  <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
                    {courseData.curriculum.map((module) => {
                      const isOpen = openModule === module.moduleNumber;
                      return (
                        <div key={module.moduleNumber} className="bg-white">
                          <button
                            type="button"
                            onClick={() => toggleModule(module.moduleNumber)}
                            className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer text-left"
                          >
                            <span className="font-bold text-[#1A1A1A] text-[20px] text-left">
                              Module {module.moduleNumber}: {module.title}
                            </span>
                            <ChevronDown
                              size={20}
                              className={`text-gray-400 transition-transform ${isOpen ? "rotate-180 text-[#1B61FF]" : ""}`}
                            />
                          </button>

                          {isOpen ? (
                            <div className="px-6 pb-6">
                              {module.items && module.items.length > 0 ? (
                                <div className="space-y-4">
                                  <div className="space-y-3">
                                    {module.items.map((item, index) => (
                                      <div key={index} className="flex gap-3 items-start">
                                        <CheckCircle size={20} className="text-[#1B61FF] flex-shrink-0 mt-1" />
                                        <span className="text-[18px] text-[#666666]">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {module.description && (
                                    <div className="text-[18px] text-[#666666] leading-relaxed pt-3 border-t border-gray-200">
                                      {module.description}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-[18px] text-[#666666] leading-relaxed">
                                  {module.description || `This module focuses on ${module.title.toLowerCase()} and includes guided learning outcomes.`}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              {courseData?.eligibilityAndCertification ? (
                <section className="space-y-8">
                  <div>
                    <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Pre-requisites:</h2>
                    <div className="space-y-3">
                      {(courseData.eligibilityAndCertification.prerequisites || []).map((item) => (
                        <div key={item} className="flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Certification:</h2>
                    <p className="text-[#666666] leading-relaxed text-[18px] mb-6">
                      {courseData.eligibilityAndCertification.certification?.intro}
                    </p>
                    <div className="space-y-3 mb-6">
                      {(courseData.eligibilityAndCertification.certification?.items || []).map((item) => (
                        <div key={item} className="flex gap-3 text-[#666666] text-[18px]">
                          <CheckCircle size={18} className="text-[#1B61FF] shrink-0 mt-1" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                    {courseData.eligibilityAndCertification.certification?.link ? (
                      <p className="text-[#666666] leading-relaxed text-[18px]">
                        For more information on the certificate, visit:{" "}
                        <Link
                          href={courseData.eligibilityAndCertification.certification.link}
                          className="text-[#1B61FF] hover:underline break-words"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {courseData.eligibilityAndCertification.certification.link}
                        </Link>
                      </p>
                    ) : null}
                  </div>
                </section>
              ) : (
                <section>
                  <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-4">Who This Is For</h2>
                  <p className="text-[#666666] leading-relaxed text-[18px]">
                    {courseData?.whoIsItFor}
                  </p>
                </section>
              )}

              <section>
                <h2 className="text-[#1A1A1A] text-xl md:text-2xl font-bold mb-6">Career Outcomes</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(courseData?.careerOutcomes || []).map((outcome) => (
                    <article
                      key={outcome.role}
                      className="bg-white overflow-hidden rounded-xl border border-gray-200 hover:shadow-xl transition-all group flex flex-col p-6"
                    >
                      <h3 className="text-[#1A1A1A] text-lg font-bold mb-2 group-hover:text-[#1B61FF] transition-colors leading-tight">
                        {outcome.role}
                      </h3>
                      <p className="text-[#1B61FF] font-extrabold text-sm md:text-base">
                        {outcome.salaryRange}
                      </p>
                    </article>
                  ))}
                </div>
              </section>

              <CourseTestimonialsMini courseSlug={resolvedCourseSlug} />

              <div className="mt-12 space-y-12">
                <CourseRatingWidget
                  averageRating={courseData?.averageRating ?? courseData?.rating?.average}
                  totalReviews={courseData?.totalReviews ?? courseData?.rating?.totalReviews}
                  ratingBreakdown={courseData?.ratingBreakdown ?? courseData?.rating?.breakdown}
                  courseName={courseName}
                  onSchemaReady={handleRatingSchemaReady}
                />

                <SocialShareWidget
                  url={canonical}
                  title={courseData?.seo?.title || courseName}
                  description={courseData?.seo?.description}
                />
              </div>
            </div>

            <EnrollCTAWidget
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              courseData={courseData as any}
              nextBatchStarts={courseData?.nextBatchStarts}
            />
          </div>
        </div>

        <FAQWidget
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          faqs={courseData?.faq as any}
          generateSchema={true}
          className="pt-0"
          subtitle="Have questions before enrolling? Here are quick answers to what learners ask most often."
        />

        <RelatedContentWidget
          title="You Might Also Like"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          courses={(courseData?.relatedCourses || []) as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          blogs={(courseData?.relatedBlogs || []) as any}
        />
      </div>
    </>
  );
}

